import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LayoutDashboard, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import BrunchiWordmark from "@/components/BrunchiWordmark";

export default function Header() {
  const { setOpen: setCartOpen, count } = useCart();
  const { user } = useAuth();
  const { lang, setLang, t } = useT();
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();

  const NAV = [
    { to: "/", key: "nav.home" },
    { to: "/corporate-catering", key: "nav.catering" },
    { to: "/shop", key: "nav.shop", children: [
      { to: "/shop", key: "nav.shop_all" },
      { to: "/bundle", key: "nav.bundle" },
      { to: "/wholesale", key: "nav.wholesale" },
    ] },
    { to: "/gift-card", key: "nav.gift" },
    { to: "/blog", key: "nav.blog" },
    { to: "/contact", key: "nav.contact" },
  ];
  const linkCls = "text-sm font-medium text-[color:var(--brunchi-ink-soft)] hover:text-[color:var(--brunchi-green-hover)]";

  const LangToggle = ({ testid }) => (
    <div className="inline-flex items-center rounded-full border border-[color:var(--brunchi-stone)] overflow-hidden text-[11px] font-mono" data-testid={testid}>
      <button onClick={() => setLang("en")}
        className={`px-2.5 py-1 ${lang === "en" ? "bg-[color:var(--brunchi-ink)] text-white" : "text-[color:var(--brunchi-ink-soft)]"}`}
        data-testid="lang-en-btn">EN</button>
      <button onClick={() => setLang("el")}
        className={`px-2.5 py-1 ${lang === "el" ? "bg-[color:var(--brunchi-ink)] text-white" : "text-[color:var(--brunchi-ink-soft)]"}`}
        data-testid="lang-el-btn">EL</button>
    </div>
  );

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[color:var(--brunchi-paper)]/90 border-b border-[color:var(--brunchi-stone)]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative flex items-center justify-between h-20 lg:h-28">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <BrunchiWordmark size={90} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
          {NAV.map((n) => n.children ? (
            <div key={n.to} className="relative group" data-testid="nav-shop-menu">
              <Link to={n.to} className={`${linkCls} inline-flex items-center gap-1`} data-testid={`nav-${n.key}`}>
                {t(n.key)} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
              </Link>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity">
                <div className="min-w-[180px] rounded-xl border border-[color:var(--brunchi-stone)] bg-[color:var(--brunchi-paper)] shadow-lg py-2">
                  {n.children.map((c) => (
                    <Link key={c.key} to={c.to}
                      className="block px-4 py-2 text-sm text-[color:var(--brunchi-ink)] hover:bg-[color:var(--brunchi-cream-2)] hover:text-[color:var(--brunchi-green-hover)]"
                      data-testid={`nav-${c.key}`}>
                      {t(c.key)}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Link key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.key}`}>
              {t(n.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><LangToggle testid="lang-toggle-desktop" /></div>
          {user?.is_admin && (
            <button onClick={() => nav("/admin")}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--brunchi-forest)] hover:text-[color:var(--brunchi-green-hover)] px-3 py-2 rounded-full"
              data-testid="admin-link">
              <LayoutDashboard size={16} /> {t("nav.admin")}
            </button>
          )}
          <button onClick={() => nav(user ? "/account" : "/auth")}
            className="p-2 rounded-full hover:bg-[color:var(--brunchi-cream-2)]"
            data-testid="account-btn" aria-label={t("nav.account")}>
            <User size={20} />
          </button>
          <button onClick={() => setCartOpen(true)}
            className="relative p-2 rounded-full hover:bg-[color:var(--brunchi-cream-2)]"
            data-testid="cart-toggle-btn" aria-label={t("nav.cart")}>
            <ShoppingBag size={20} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[color:var(--brunchi-green)] text-white text-[10px] font-mono w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
          <button className="lg:hidden p-2" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-btn">
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden border-t border-[color:var(--brunchi-stone)] bg-[color:var(--brunchi-paper)] px-4 py-3 space-y-2" data-testid="mobile-menu">
          {NAV.map((n) => (
            <div key={n.to}>
              <Link to={n.to} onClick={() => setMobile(false)}
                className="block py-2 text-base font-medium text-[color:var(--brunchi-ink)]"
                data-testid={`mobile-nav-${n.key}`}>
                {t(n.key)}
              </Link>
              {n.children?.filter((c) => c.to !== n.to).map((c) => (
                <Link key={c.key} to={c.to} onClick={() => setMobile(false)}
                  className="block py-1.5 pl-4 text-sm text-[color:var(--brunchi-ink-soft)]"
                  data-testid={`mobile-nav-${c.key}`}>
                  {t(c.key)}
                </Link>
              ))}
            </div>
          ))}
          {user?.is_admin && (
            <Link to="/admin" onClick={() => setMobile(false)} className="block py-2 text-base font-medium text-[color:var(--brunchi-forest)]" data-testid="mobile-admin-link">
              {t("nav.admin")}
            </Link>
          )}
          <div className="pt-2"><LangToggle testid="lang-toggle-mobile" /></div>
        </div>
      )}
    </header>
  );
}
